import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { authAPI } from '../../services/api';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Verifying your email...');

  useEffect(() => {
    let cancelled = false;

    const verify = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link.');
        return;
      }

      try {
        const response = await authAPI.verifyEmail(token);
        if (!cancelled) {
          setStatus('success');
          setMessage(response.data.message || 'Email verified successfully. You can now sign in.');
        }
      } catch (error: any) {
        if (!cancelled) {
          setStatus('error');
          setMessage(error.response?.data?.error || 'Email verification failed.');
        }
      }
    };

    verify();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 text-center">
        {status === 'loading' && (
          <p className="text-sm text-gray-600">{message}</p>
        )}
        {status === 'success' && (
          <>
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-600" />
            <h2 className="text-2xl font-bold text-gray-900">Email verified</h2>
            <p className="text-sm text-gray-600">{message}</p>
            <Link
              to="/login"
              className="inline-flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Sign in
            </Link>
          </>
        )}
        {status === 'error' && (
          <>
            <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-500" />
            <h2 className="text-2xl font-bold text-gray-900">Verification failed</h2>
            <p className="text-sm text-gray-600">{message}</p>
            <Link
              to="/login"
              className="inline-flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              Back to sign in
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmail;
